// ──────────────────────────────────────────────────────────────────────────
// Sparkline — animated mini-chart for KPI cards.
//
//   <Sparkline data={[1, 4, 2, 7, 3, ...]} color="#6366f1" />
//
// Draws a smoothed polyline + soft area fill, animated in with a path
// dash-offset trick. Hovering shows a value tooltip with the date label.
//
// `data` accepts either:
//   - an array of numbers, or
//   - an array of { date: 'YYYY-MM-DD', value: <number> }
// ──────────────────────────────────────────────────────────────────────────

import React, { useId, useMemo, useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const draw = keyframes`
  from { stroke-dashoffset: var(--len); }
  to   { stroke-dashoffset: 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const Wrap = styled.div`
  position: relative;
  width: 100%;
  height: ${p => p.$h}px;

  svg { display: block; width: 100%; height: 100%; overflow: visible; }
`;

const PathLine = styled.path`
  fill: none;
  stroke: ${p => p.$color};
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: var(--len);
  stroke-dashoffset: var(--len);
  animation: ${draw} 1.1s cubic-bezier(0.65, 0, 0.35, 1) 0.05s forwards;
  filter: drop-shadow(0 1px 1.5px ${p => p.$color}55);
`;

const Area = styled.path`
  opacity: 0;
  animation: ${fadeIn} 700ms ease 700ms forwards;
`;

const Dot = styled.circle`
  opacity: 0;
  animation: ${fadeIn} 280ms ease 1.1s forwards;
  filter: drop-shadow(0 0 4px ${p => p.$color}aa);
`;

const Tooltip = styled.div`
  position: absolute;
  top: -34px;
  transform: translateX(-50%);
  background: #0f172a;
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0,0,0,0.25);
  z-index: 5;

  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 6px; height: 6px;
    background: #0f172a;
  }
`;

const HoverGuide = styled.line`
  stroke: ${p => p.$color};
  stroke-width: 1;
  stroke-dasharray: 2 3;
  opacity: 0.5;
`;

/**
 * Build a smoothed cubic-Bézier path through `points`.
 * Tension keeps the curve gentle (0.18 looks great for sparklines).
 */
const smoothPath = (points, tension = 0.18) => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) * tension;
    const c1y = p1.y + (p2.y - p0.y) * tension;
    const c2x = p2.x - (p3.x - p1.x) * tension;
    const c2y = p2.y - (p3.y - p1.y) * tension;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d;
};

const formatDateShort = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatVal = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return Math.round(n).toLocaleString();
};

export const Sparkline = ({
  data = [],
  color = '#6366f1',
  height = 44,
  showLastDot = true,
  ariaLabel,
}) => {
  const id = useId().replace(/:/g, '');
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null); // { idx, x, y }
  const [width, setWidth] = useState(160);

  // Resize observer so the sparkline rescales when the card resizes.
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 160;
      setWidth(Math.max(40, Math.floor(w)));
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const normalised = useMemo(() => {
    return (data || []).map((d, i) => {
      if (typeof d === 'number') return { value: d, date: null, i };
      return { value: Number(d?.value) || 0, date: d?.date || null, i };
    });
  }, [data]);

  const { d, areaD, points, len } = useMemo(() => {
    const n = normalised.length;
    const padX = 2, padTop = 4, padBot = 4;
    const w = width;
    const h = height;
    if (n === 0) return { d: '', areaD: '', points: [], len: 0, min: 0, max: 0 };

    const values = normalised.map(p => p.value);
    let mn = Math.min(...values);
    let mx = Math.max(...values);
    if (mn === mx) { mn = mn - 1; mx = mx + 1; }

    const xs = (i) => padX + (i / Math.max(1, n - 1)) * (w - padX * 2);
    const ys = (v) => padTop + (1 - (v - mn) / (mx - mn)) * (h - padTop - padBot);

    const pts = normalised.map((p, i) => ({ x: xs(i), y: ys(p.value), value: p.value, date: p.date }));
    const path = smoothPath(pts, 0.18);
    const area = `${path} L ${pts[pts.length - 1].x} ${h - padBot} L ${pts[0].x} ${h - padBot} Z`;

    // approximate the path length so the draw animation knows when to stop
    let approxLen = 0;
    for (let i = 1; i < pts.length; i++) {
      const dx = pts[i].x - pts[i - 1].x;
      const dy = pts[i].y - pts[i - 1].y;
      approxLen += Math.hypot(dx, dy);
    }
    return { d: path, areaD: area, points: pts, len: Math.ceil(approxLen * 1.05), min: mn, max: mx };
  }, [normalised, width, height]);

  // Empty state — flat hairline so the card doesn't look broken.
  if (!points.length) {
    return (
      <Wrap ref={wrapRef} $h={height} aria-hidden="true">
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          <line x1="0" y1={height / 2} x2={width} y2={height / 2}
            stroke="#e2e8f0" strokeWidth="1.2" strokeDasharray="3 4" />
        </svg>
      </Wrap>
    );
  }

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    // pick nearest point by x
    let nearest = 0;
    let bestDist = Infinity;
    for (let i = 0; i < points.length; i++) {
      const dist = Math.abs(points[i].x - (x / rect.width) * width);
      if (dist < bestDist) { bestDist = dist; nearest = i; }
    }
    setHover({ idx: nearest, x: points[nearest].x, y: points[nearest].y });
  };

  const last = points[points.length - 1];
  const tip = hover ? points[hover.idx] : null;
  const tipLeftPct = tip ? (tip.x / width) * 100 : 0;

  return (
    <Wrap
      ref={wrapRef}
      $h={height}
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
      role="img"
      aria-label={ariaLabel || `Sparkline, ${points.length} data points, last value ${last.value}`}
    >
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id={`grad-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* baseline so empty days don't read as zero */}
        <line x1="0" y1={height - 4} x2={width} y2={height - 4}
          stroke="#f1f5f9" strokeWidth="1" />

        <Area d={areaD} fill={`url(#grad-${id})`} />
        <PathLine d={d} $color={color} style={{ '--len': `${len}px` }} />

        {showLastDot && (
          <Dot cx={last.x} cy={last.y} r="2.6" fill={color} $color={color} />
        )}

        {hover && tip && (
          <>
            <HoverGuide x1={tip.x} y1={2} x2={tip.x} y2={height - 2} $color={color} />
            <circle cx={tip.x} cy={tip.y} r="3.5" fill="white" stroke={color} strokeWidth="1.6" />
          </>
        )}
      </svg>
      {hover && tip && (
        <Tooltip style={{ left: `${tipLeftPct}%`, top: `${Math.max(0, tip.y - 32)}px` }}>
          {formatVal(tip.value)}{tip.date ? ` · ${formatDateShort(tip.date)}` : ''}
        </Tooltip>
      )}
    </Wrap>
  );
};

export default Sparkline;
